import React, { useState, useEffect } from 'react';
import { Table, Space, Modal, DatePicker, Input, message, Button, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { getWithdrawalPassedLoans, updateInvoiceData } from '../../services/loanService';
import { formatCurrency, formatDate, formatPercentage } from '../../utils/formatters';
import Page from '../../components/layout/Page';
import locale from 'antd/es/date-picker/locale/de_DE';
import { Link } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/de';
import { FileTextOutlined, BankOutlined, SearchOutlined } from '@ant-design/icons';

moment.locale('de');

const WithdrawalPassed = () => {
    const { t } = useTranslation();
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedLoan, setSelectedLoan] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filteredLoans, setFilteredLoans] = useState([]);
    
    // Modal states
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showStatementModal, setShowStatementModal] = useState(false);
    const [invoiceData, setInvoiceData] = useState({
        invoice_date: null,
        invoice_number: ''
    });
    const [statementData, setStatementData] = useState({
        statement_received_date: null,
        statement_received_number: ''
    });

    useEffect(() => {
        fetchLoans();
    }, []);

    useEffect(() => {
        // Filter loans based on search text
        const filtered = loans.filter(loan => {
            const searchFields = [
                loan.process_number,
                loan.customer?.name,
                loan.customer?.first_name,
                loan.primary_consultant?.name,
                loan.bank?.name,
                loan.pooler?.name,
                loan.amount?.toString(),
            ].filter(Boolean).map(field => field.toLowerCase());

            return searchText === '' || searchFields.some(field => field.includes(searchText.toLowerCase()));
        });
        setFilteredLoans(filtered);
    }, [searchText, loans]);

    const fetchLoans = async () => {
        setLoading(true);
        try {
            const data = await getWithdrawalPassedLoans();
            setLoans(data);
        } catch (error) {
            message.error('Fehler beim Laden der Kredite');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleInvoiceSubmit = async () => {
        if (!invoiceData.invoice_date || !invoiceData.invoice_number) {
            message.error('Bitte füllen Sie alle Felder aus');
            return;
        }

        try {
            await updateInvoiceData(selectedLoan.id, {
                invoice_date: moment(invoiceData.invoice_date).format('YYYY-MM-DD'),
                invoice_number: invoiceData.invoice_number,
                type: 'invoice_number'
            });
            message.success('Rechnungsdaten erfolgreich aktualisiert');
            setShowInvoiceModal(false);
            fetchLoans();
        } catch (error) {
            message.error('Fehler beim Aktualisieren der Rechnungsdaten');
            console.error(error);
        }
    };

    const handleStatementSubmit = async () => {
        if (!statementData.statement_received_date || !statementData.statement_received_number) {
            message.error('Bitte füllen Sie alle Felder aus');
            return;
        }

        try {
            await updateInvoiceData(selectedLoan.id, {
                statement_received_date: moment(statementData.statement_received_date).format('YYYY-MM-DD'),
                statement_received_number: statementData.statement_received_number,
                type: 'statement'
            });
            message.success('Abrechnungsdaten erfolgreich aktualisiert');
            setShowStatementModal(false);
            fetchLoans();
        } catch (error) {
            message.error('Fehler beim Aktualisieren der Abrechnungsdaten');
            console.error(error);
        }
    };

    const columns = [
        {
            title: t('loan.fields.primaryConsultant'),
            dataIndex: 'primary_consultant',
            key: 'primary_consultant',
            render: (consultant) => `${consultant.first_name} ${consultant.last_name}`  || '-'
        },        
        {
            title: t('loan.fields.processNumber'),
            dataIndex: 'process_number',
            key: 'process_number',
            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
        },

        {
            title: t('loan.fields.customer'),
            dataIndex: 'customer',
            key: 'customer',
            render: (customer) => `${customer.name}${customer.first_name ? `, ${customer.first_name}` : ''}`
        },
        {
            title: t('loan.fields.signatureDate'),
            dataIndex: 'signature_date',
            key: 'signature_date',
            render: (date) => date ? moment(date).format('DD.MM.YYYY') : '-'
        },
        {
            title: t('loan.fields.amount'),
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (amount) => formatCurrency(amount)
        },
        {
            title: t('loan.fields.provision'),
            dataIndex: 'provision',
            key: 'provision',
            align: 'right',
            render: (provision) => formatPercentage(provision)
        },
        {
            title: t('loan.fields.bank'),
            dataIndex: 'bank',
            key: 'bank',
            render: (bank) => bank?.name || '-'
        },
        {
            title: t('loan.fields.pooler'),
            dataIndex: 'pooler',
            key: 'pooler',
            render: (pooler) => pooler?.name || '-'
        },
        {
            title: t('loan.fields.incentive_amount'),
            dataIndex: 'incentive_amount',
            key: 'incentive_amount',
            align: 'right',
            render: (amount) => amount ? formatCurrency(amount) : '-'
        },   
        {
            title: t('loan.fields.invoice_comment'),
            dataIndex: 'invoice_comment',
            key: 'invoice_comment',
            render: (comment) => comment || '-'
        },   
        {
            title: t('common.actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        onClick={() => {
                            setSelectedLoan(record);
                            setShowInvoiceModal(true);
                        }}
                        icon={<FileTextOutlined />}
                    >
                        {t('withdrawalPassed.addInvoice')}
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => {
                            setSelectedLoan(record);
                            setShowStatementModal(true);
                        }}
                        icon={<BankOutlined />}
                    >
                        {t('withdrawalPassed.addStatement')}
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <Page
            title={t('withdrawalPassed.title')}
            subtitle={t('withdrawalPassed.subtitle')}
        >
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={12} lg={8}>
                    <Input
                        placeholder={t('common.search')}
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        style={{ width: '100%' }}
                    />
                </Col>
            </Row>
            
            <div style={{ 
                width: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                marginTop: 16
            }}>
                <Table
                    dataSource={filteredLoans}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                />
            </div>

            {/* Invoice Modal */}
            <Modal
                title={t('withdrawalPassed.addInvoice')}
                open={showInvoiceModal}
                footer={[
                    <Button
                        key="cancel"
                        type="secondary"
                        onClick={() => setShowInvoiceModal(false)}
                    >
                        {t('common.cancel')}
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={handleInvoiceSubmit}
                    >
                        {t('common.save')}
                    </Button>
                ]}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <DatePicker
                        locale={locale}
                        placeholder={t('common.invoiceDate')}
                        onChange={(date) => setInvoiceData({ ...invoiceData, invoice_date: date })}
                        style={{ width: '100%' }}
                    />
                    <Input
                        placeholder={t('common.invoiceNumber')}
                        value={invoiceData.invoice_number}
                        onChange={(e) => setInvoiceData({ ...invoiceData, invoice_number: e.target.value })}
                    />
                </Space>
            </Modal>

            {/* Statement Modal */}
            <Modal
                title={t('withdrawalPassed.addStatement')}
                open={showStatementModal}
                footer={[
                    <Button
                        key="cancel"
                        variant="secondary"
                        onClick={() => setShowStatementModal(false)}
                    >
                        {t('common.cancel')}
                    </Button>,
                    <Button
                        key="submit"
                        variant="primary"
                        onClick={handleStatementSubmit}
                    >
                        {t('common.save')}
                    </Button>
                ]}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <DatePicker
                        locale={locale}
                        placeholder={t('common.statementReceivedDate')}
                        onChange={(date) => setStatementData({ ...statementData, statement_received_date: date })}
                        style={{ width: '100%' }}
                    />
                    <Input
                        placeholder={t('common.statementReceivedNumber')}
                        value={statementData.statement_received_number}
                        onChange={(e) => setStatementData({ ...statementData, statement_received_number: e.target.value })}
                    />
                </Space>
            </Modal>
        </Page>
    );
};

export default WithdrawalPassed;
